import { el, fmt, wait, type Nav, type Screen } from "./dom";
import { buildTopbar } from "./topbar";
import { i18n, t } from "../i18n";
import { ABILITIES, CHARGE_MAX } from "../game/engine";
import { GameSession, type SessionMode } from "../game/session";
import { allObjectives } from "../game/objectives";
import { getStage, nextStageId } from "../game/stages";
import { getPuzzle, nextPuzzleId, puzzleSeed } from "../game/puzzles";
import { dailySeed, todayKey } from "../game/daily";
import { MAX_TIER, tier } from "../game/tiers";
import { progress } from "../store/progress";
import { settings, ANIM_SPEED_MULT } from "../store/settings";
import { BoardRenderer, tierSwatch } from "../render/renderer";
import { fxHammer, fxPromote, fxReroll, fxSwap } from "../render/abilityFx";
import { sfxClear, sfxDrop, sfxFail, sfxMerge, unlockAudio } from "../audio/sfx";
import { submitRun } from "../net/sync";
import { showAchievementToasts } from "./toast";
import type { AbilityId, ObjectiveType, Pos, ResolveResult, Stage } from "../game/types";

const ABILITY_ORDER: AbilityId[] = ["hammer", "swap", "promote", "reroll"];
const BASE_FRAME_MS: Record<string, number> = { land: 60, gravity: 150, merge: 220 };

function frameMs(kind: string): number {
  return (BASE_FRAME_MS[kind] ?? 150) * ANIM_SPEED_MULT[settings.get("animSpeed")];
}

function haptic(pattern: number | number[]): void {
  if (settings.get("haptics") && typeof navigator !== "undefined" && navigator.vibrate) {
    navigator.vibrate(pattern);
  }
}

interface Targeting {
  id: AbilityId;
  need: number;
  picks: Pos[];
}

export class PlayScreen implements Screen {
  private session!: GameSession;
  private renderer!: BoardRenderer;
  private host!: HTMLElement;
  private off?: () => void;
  private mode: SessionMode;
  private stage: Stage | null;
  private daily: { dateKey: string } | null = null;
  private selectedCol = 0;
  private locked = false;
  private targeting: Targeting | null = null;
  private resultShown = false;

  /** Set when playing a puzzle — drives the fixed seed + puzzle next-chain. */
  private puzzleId: string | null = null;

  constructor(
    private nav: Nav,
    opts: { stageId?: string; daily?: boolean; puzzleId?: string },
  ) {
    this.puzzleId = opts.puzzleId ?? null;
    this.stage = this.puzzleId
      ? getPuzzle(this.puzzleId) ?? null
      : opts.stageId
        ? getStage(opts.stageId) ?? null
        : null;
    // One board width for every mode, picked on the home screen and clamped to
    // what the player has unlocked. Puzzles always use their authored width
    // (configFromStage ignores the override when presetCells exist).
    const cols = progress.effectiveCols(settings.get("boardCols"));
    if (opts.daily) {
      this.daily = { dateKey: todayKey() };
      this.mode = { kind: "daily", cols };
    } else if (this.stage) {
      this.mode = { kind: "campaign", stage: this.stage, cols };
    } else {
      this.mode = { kind: "endless", cols };
    }
  }

  private get showsDrops(): boolean {
    return !!this.stage || !!this.daily;
  }

  private get isTimed(): boolean {
    return !!this.stage && this.stage.constraints.timeLimit != null;
  }

  /** Format seconds as M:SS for the HUD timer. */
  private formatTime(s: number): string {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  }

  mount(host: HTMLElement): void {
    this.host = host;
    this.start();
    this.off = i18n.onChange(() => this.renderStaticText());
    window.addEventListener("keydown", this.onKey);
    // Time-limited stages need a clock — re-evaluate every 250ms so the timer
    // ticks down visibly and the run ends on schedule even if the player has
    // stopped dropping.
    if (this.isTimed) this.startTimer();
    // First-time players get a short coach-mark walkthrough (idea #13).
    if (!progress.snapshot.tutorialDone) this.showTutorial();
  }

  /** 3-step dismissible tutorial overlay for brand-new players. */
  private showTutorial(): void {
    const steps = [
      { icon: "👇", key: "tut.drop" },
      { icon: "✨", key: "tut.merge" },
      { icon: "🌌", key: "tut.evolve" },
    ];
    let i = 0;

    const backdrop = el("div", "tutorial-backdrop");
    const card = el("div", "tutorial-card");
    backdrop.appendChild(card);

    const renderStep = (): void => {
      const s = steps[i];
      card.innerHTML = `
        <div class="tut-icon">${s.icon}</div>
        <div class="tut-text">${t(s.key)}</div>
        <div class="tut-dots">${steps
          .map((_, d) => `<span class="tut-dot ${d === i ? "on" : ""}"></span>`)
          .join("")}</div>`;
      const next = el(
        "button",
        "btn-primary tut-next",
        t(i < steps.length - 1 ? "tut.next" : "tut.start"),
      );
      next.onclick = () => {
        if (i < steps.length - 1) {
          i++;
          renderStep();
        } else {
          progress.markTutorialDone();
          backdrop.remove();
        }
      };
      const skip = el("button", "tut-skip", t("tut.skip"));
      skip.onclick = () => {
        progress.markTutorialDone();
        backdrop.remove();
      };
      card.append(next, skip);
    };
    renderStep();
    this.host.appendChild(backdrop);
  }

  unmount(): void {
    this.off?.();
    window.removeEventListener("keydown", this.onKey);
    this.stopTimer();
  }

  private timer: ReturnType<typeof setInterval> | null = null;

  private startTimer(): void {
    this.stopTimer();
    this.timer = setInterval(() => {
      if (!this.session) return;
      this.session.tick();
      this.updateHud();
      if (this.session.finished && !this.resultShown) this.onFinish();
    }, 250);
  }

  private stopTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private start(): void {
    this.session = new GameSession(
      this.mode,
      this.freshSeed(),
      progress.effects(),
      settings.get("previewCount"),
    );
    this.renderer = new BoardRenderer(this.session.engine.grid);
    this.renderer.setLabelMode(settings.get("tierNotation"));
    this.selectedCol = Math.floor(this.session.engine.config.cols / 2);
    this.locked = false;
    this.targeting = null;
    this.resultShown = false;
    this.stopTimer();
    if (this.isTimed) this.startTimer();
    this.buildUI();
    this.renderer.render(this.session.engine.grid);
    this.updateHud();
    this.highlight();
  }

  private freshSeed(): string {
    if (this.daily) return dailySeed(this.daily.dateKey); // fixed board for everyone
    // Puzzles use a fixed seed: the spawn queue is part of the puzzle design,
    // so every player gets the identical board AND queue (shareable solutions).
    if (this.puzzleId) return puzzleSeed(this.puzzleId);
    const ref = this.stage?.id ?? "endless";
    return `${ref}-${performance.now().toString(36)}`;
  }

  private get abilityIds(): AbilityId[] {
    const allowed = this.session.allowedAbilities;
    return allowed ? ABILITY_ORDER.filter((id) => allowed.includes(id)) : ABILITY_ORDER;
  }

  // ---- UI ----
  private buildUI(): void {
    this.host.innerHTML = "";
    const screen = el("div", "screen play");
    const top = buildTopbar({
      title: this.daily ? "menu.daily" : this.puzzleId ? "menu.puzzles" : this.stage ? undefined : "menu.endless",
      onBack: () =>
        this.puzzleId ? this.nav.puzzles() : this.stage ? this.nav.campaign() : this.nav.home(),
      showStardust: true,
    });
    if (this.stage && !this.puzzleId) {
      const h = top.node.querySelector(".brand");
      if (h) h.innerHTML = `<h1>${this.stage.id}</h1>`;
    }

    const hud = el("div", "panel hud");
    hud.innerHTML = `
      <div class="stat"><span class="k" data-i18n="hud.score"></span><span class="v" id="score">0</span></div>
      <div class="stat"><span class="k" id="meter-k"></span><span class="v" id="meter-v">0</span></div>
      <div class="stat"><span class="k" data-i18n="hud.chain"></span><span class="v" id="chain">0</span></div>
      <div class="next-queue">
        <div class="queue-slot current-slot">
          <span class="k" data-i18n="hud.current"></span>
          <div class="q q-current" id="queue-current"></div>
        </div>
        <div class="queue-slot upcoming-slot">
          <span class="k" data-i18n="hud.next"></span>
          <div class="q q-upcoming" id="queue-upcoming"></div>
        </div>
      </div>`;

    const ladder = this.buildEvoLadder();

    const objectives = el("div", "panel objectives");
    objectives.id = "objectives";
    if (!this.stage) objectives.style.display = "none";

    const gauge = el("div", "gauge-row");
    gauge.innerHTML = `
      <span class="k" data-i18n="hud.charge"></span>
      <div class="gauge"><div class="gauge-fill" id="gauge-fill"></div></div>`;

    const boardWrap = el("div", "board-wrap");
    boardWrap.id = "board-wrap";
    boardWrap.appendChild(this.renderer.svg);

    const abilityBar = el("div", "ability-bar");
    abilityBar.id = "ability-bar";
    for (const id of this.abilityIds) {
      const btn = el("button", "ability-btn");
      btn.dataset.ability = id;
      btn.innerHTML = `<span class="ab-name" data-i18n="ability.${id}"></span><span class="ab-cost">${this.session.engine.abilityCost(id)}</span>`;
      btn.onclick = () => this.onAbilityClick(id);
      abilityBar.appendChild(btn);
    }

    const hint = el("div", "hint");
    hint.id = "hint";

    screen.append(top.node, hud, ladder, objectives, gauge, boardWrap, abilityBar, hint);
    this.host.appendChild(screen);
    this.topRefresh = top.refresh;
    this.attachInput();
    this.renderStaticText();
  }

  /** The 12-tier evolution ladder shown at the top (replaces tile numbers). */
  private buildEvoLadder(): HTMLElement {
    const wrap = el("div", "evo-ladder");
    const track = el("div", "evo-track");
    for (let lv = 1; lv <= MAX_TIER; lv++) {
      // All 12 tiers stay fully active; discovery no longer dims them.
      const cell = el("span", "evo-cell reached");
      cell.dataset.lv = String(lv);
      cell.innerHTML = tierSwatch(lv, 30);
      track.appendChild(cell);
    }
    const name = el("div", "evo-name");
    name.id = "evo-name";
    wrap.append(track, name);
    return wrap;
  }

  private updateLadder(): void {
    // Mark the piece the player is about to drop; all tiers stay active.
    const currentTier = this.session.engine.current;
    const track = this.host.querySelector(".evo-track");
    if (track) {
      for (const cell of Array.from(track.children) as HTMLElement[]) {
        const lv = Number(cell.dataset.lv);
        cell.classList.toggle("current", lv === currentTier);
      }
    }
    const name = document.getElementById("evo-name");
    if (name) {
      // The current piece can be a special (wild = -1, bomb = -2), which has no
      // tier entry — guard tier() so the ladder never throws and aborts render.
      name.textContent =
        currentTier > 0
          ? t(`tier.${tier(currentTier).key}`)
          : currentTier === -1
            ? t("collection.wild.name")
            : currentTier === -2
              ? t("collection.bomb.name")
              : "";
    }
  }

  private topRefresh: () => void = () => {};

  private renderStaticText(): void {
    this.topRefresh();
    this.host.querySelectorAll<HTMLElement>("[data-i18n]").forEach((n) => {
      n.textContent = t(n.dataset.i18n!);
    });
    const meterK = document.getElementById("meter-k");
    if (meterK) {
      meterK.textContent = this.isTimed
        ? t("hud.time")
        : this.showsDrops
          ? t("hud.drops")
          : t("hud.best");
    }
    this.updateLadder();
    this.updateObjectives();
    this.updateHint();
  }

  // ---- Input ----
  // The piece is released on pointer-UP at the finger's position (not on
  // press), so the player can slide to fine-tune the column and the drop lands
  // exactly under where they lift off.
  private pressing = false;

  private attachInput(): void {
    const svg = this.renderer.svg;
    svg.addEventListener("pointermove", (e) => {
      if (this.targeting || this.locked) return;
      this.selectedCol = this.colFromClient(e.clientX);
      this.highlight();
    });
    svg.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    svg.addEventListener("pointerup", (e) => this.onPointerUp(e));
    svg.addEventListener("pointercancel", () => {
      this.pressing = false;
    });
  }

  private colFromClient(clientX: number): number {
    return this.renderer.colFromClient(clientX);
  }

  private onPointerDown(e: PointerEvent): void {
    unlockAudio(); // first user gesture enables the AudioContext
    if (this.locked || this.session.finished || this.targeting) return;
    this.pressing = true;
    this.selectedCol = this.colFromClient(e.clientX);
    this.highlight();
  }

  private onPointerUp(e: PointerEvent): void {
    if (this.locked || this.session.finished) return;
    if (this.targeting) {
      const cell = this.renderer.cellFromClient(e.clientX, e.clientY);
      if (cell) this.onTargetPick(cell);
      return;
    }
    if (!this.pressing) return;
    this.pressing = false;
    this.selectedCol = this.colFromClient(e.clientX);
    void this.doDrop();
  }

  private onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape" && this.targeting) {
      this.cancelTargeting();
      return;
    }
    if (this.locked || this.targeting || this.session.finished) return;
    const cols = this.session.engine.config.cols;
    if (e.key === "ArrowLeft") {
      this.selectedCol = Math.max(0, this.selectedCol - 1);
      this.highlight();
    } else if (e.key === "ArrowRight") {
      this.selectedCol = Math.min(cols - 1, this.selectedCol + 1);
      this.highlight();
    } else if (e.key === "ArrowDown" || e.key === " ") {
      e.preventDefault();
      void this.doDrop();
    }
  };

  private highlight(): void {
    const show = !this.targeting && !this.session.finished && !this.locked;
    this.renderer.setColumnHighlight(show ? this.selectedCol : null);
  }

  // ---- Drop ----
  /** Captured bounding rect of the upcoming-slot's first child, used by
   * updateHud() to slide that piece into the current slot after a drop. */
  private pendingSlideFrom: DOMRect | null = null;

  private async doDrop(): Promise<void> {
    if (this.locked || !this.session.canDrop()) return;
    // Capture where the "next" piece is now, so we can animate it sliding
    // into the "current" slot after the queue is rebuilt.
    const nextEl = document
      .getElementById("queue-upcoming")
      ?.firstElementChild as HTMLElement | undefined;
    this.pendingSlideFrom = nextEl?.getBoundingClientRect() ?? null;

    const result = this.session.drop(this.selectedCol);
    if (!result) {
      this.pendingSlideFrom = null;
      return;
    }
    sfxDrop();
    haptic(10);
    await this.playResult(result);
  }

  // ---- Abilities ----
  private onAbilityClick(id: AbilityId): void {
    if (this.locked || this.session.finished) return;
    if (this.targeting?.id === id) {
      this.cancelTargeting();
      return;
    }
    if (!this.session.engine.canUse(id) || !this.session.isAbilityAllowed(id)) return;

    if (id === "reroll") {
      const r = this.session.useAbility("reroll", []);
      if (r === "reroll") {
        this.updateHud();
        const target = document.getElementById("queue-current")?.firstElementChild;
        if (target) fxReroll(target);
      }
      return;
    }
    this.targeting = { id, need: ABILITIES[id].targets, picks: [] };
    this.renderer.setColumnHighlight(null);
    this.refreshAbilityButtons();
    this.updateHint();
  }

  private onTargetPick(cell: Pos): void {
    if (!this.targeting) return;
    const tg = this.targeting;
    const i = tg.picks.findIndex((p) => p.r === cell.r && p.c === cell.c);
    if (i >= 0) tg.picks.splice(i, 1);
    else tg.picks.push(cell);
    this.renderer.setTargetHighlight(tg.picks);
    if (tg.picks.length === tg.need) void this.executeAbility();
  }

  private async executeAbility(): Promise<void> {
    if (!this.targeting) return;
    const { id, picks } = this.targeting;
    const result = this.session.useAbility(id, picks);
    this.renderer.setTargetHighlight([]);
    this.targeting = null;
    this.updateHint();
    if (!result || result === "reroll") {
      this.refreshAbilityButtons();
      this.highlight();
      return;
    }
    this.playAbilityFx(id, picks);
    await this.playResult(result);
  }

  /** Trigger the SVG juice for an ability before the resolve animation runs. */
  private playAbilityFx(id: AbilityId, picks: Pos[]): void {
    const wrap = document.getElementById("board-wrap");
    if (!wrap || picks.length === 0) return;
    const a = this.renderer.cellClientPos(picks[0].r, picks[0].c);
    if (id === "hammer") {
      fxHammer(wrap, a.x, a.y);
    } else if (id === "promote") {
      fxPromote(wrap, a.x, a.y);
    } else if (id === "swap" && picks.length === 2) {
      const b = this.renderer.cellClientPos(picks[1].r, picks[1].c);
      fxSwap(wrap, a.x, a.y, b.x, b.y);
    }
  }

  private cancelTargeting(): void {
    this.targeting = null;
    this.renderer.setTargetHighlight([]);
    this.updateHint();
    this.refreshAbilityButtons();
    this.highlight();
  }

  // ---- Frame playback ----
  private async playResult(result: ResolveResult): Promise<void> {
    this.locked = true;
    this.refreshAbilityButtons();
    this.renderer.setColumnHighlight(null);

    try {
      for (const frame of result.frames) {
        this.renderer.renderCells(frame.cells);
        if (frame.kind === "merge") {
          sfxMerge(frame.chainLevel);
          if (frame.chainLevel >= 2) {
            this.showCombo(frame.chainLevel);
            this.shake(frame.chainLevel);
            haptic(frame.chainLevel >= 4 ? 40 : 18);
          }
        }
        await wait(frameMs(frame.kind));
      }
    } catch (err) {
      console.error("playResult render error", err);
    } finally {
      // Always release the lock and settle on the final board state, so a
      // transient render error can never soft-lock the game.
      this.locked = false;
      this.renderer.render(this.session.engine.grid);
      this.updateHud();
      if (this.session.finished) {
        this.onFinish();
      } else {
        this.highlight();
        this.refreshAbilityButtons();
      }
    }
  }

  // ---- HUD ----
  private updateHud(): void {
    const e = this.session.engine;
    document.getElementById("score")!.textContent = fmt(e.score);
    const meterV = document.getElementById("meter-v");
    if (meterV) {
      meterV.textContent = this.isTimed
        ? this.formatTime(this.session.secondsRemaining ?? 0)
        : this.showsDrops
          ? String(this.session.dropsRemaining ?? 0)
          : fmt(Math.max(progress.snapshot.bestEndless, e.score));
    }
    document.getElementById("chain")!.textContent = String(e.maxChain);
    const fill = document.getElementById("gauge-fill");
    if (fill) fill.style.width = `${(e.charge / CHARGE_MAX) * 100}%`;
    const cur = document.getElementById("queue-current");
    if (cur) cur.innerHTML = tierSwatch(e.current, 46);
    const up = document.getElementById("queue-upcoming");
    if (up) up.innerHTML = e.preview.map((lv) => tierSwatch(lv, 30)).join("");
    // Slide the freshly-promoted "current" piece from where it sat in the
    // upcoming row (captured before the drop) into its new slot.
    if (this.pendingSlideFrom && cur?.firstElementChild) {
      const from = this.pendingSlideFrom;
      const target = cur.firstElementChild as HTMLElement;
      const to = target.getBoundingClientRect();
      const dx = from.left + from.width / 2 - (to.left + to.width / 2);
      const dy = from.top + from.height / 2 - (to.top + to.height / 2);
      const scale = from.width / Math.max(1, to.width);
      target.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0.85 },
          { transform: "translate(0, 0) scale(1)", opacity: 1 },
        ],
        { duration: 240, easing: "cubic-bezier(0.2, 0.9, 0.3, 1.1)" },
      );
    }
    this.pendingSlideFrom = null;
    this.updateLadder();
    this.updateObjectives();
    this.refreshAbilityButtons();
  }

  private objectiveLabel(type: ObjectiveType, value: number, tier?: number): string {
    return t(`obj.${type}`, { value, tier: tier ?? 0 });
  }

  private updateObjectives(): void {
    const box = document.getElementById("objectives");
    if (!box || !this.stage) return;
    const progressList = allObjectives(this.stage, this.session.stats);
    box.innerHTML = progressList
      .map((p) => {
        const pct = Math.min(100, (p.current / p.target) * 100);
        return `
        <div class="obj-row ${p.done ? "done" : ""}">
          <span class="obj-check">${p.done ? "✓" : "○"}</span>
          <span class="obj-label">${this.objectiveLabel(
            p.objective.type,
            p.objective.value,
            p.objective.tier,
          )}</span>
          <span class="obj-count">${Math.min(p.current, p.target)}/${p.target}</span>
          <div class="obj-bar"><div class="obj-bar-fill" style="width:${pct}%"></div></div>
        </div>`;
      })
      .join("");
  }

  private refreshAbilityButtons(): void {
    const bar = document.getElementById("ability-bar");
    if (!bar) return;
    for (const btn of Array.from(bar.children) as HTMLButtonElement[]) {
      const id = btn.dataset.ability as AbilityId;
      const usable =
        !this.locked &&
        !this.session.finished &&
        this.session.isAbilityAllowed(id) &&
        this.session.engine.canUse(id);
      btn.disabled = !usable;
      btn.classList.toggle("active", this.targeting?.id === id);
    }
  }

  private updateHint(): void {
    const hint = document.getElementById("hint");
    if (!hint) return;
    if (this.targeting) {
      hint.textContent = `${t(`ability.${this.targeting.id}.hint`)} · ${t("ability.cancel")}`;
    } else {
      hint.textContent = t("hint.drop");
    }
  }

  // ---- FX ----
  private showCombo(chainLevel: number): void {
    const wrap = document.getElementById("board-wrap");
    if (!wrap) return;
    const pop = el("div", "combo-pop");
    if (chainLevel >= 4) pop.classList.add("combo-epic");
    else if (chainLevel >= 3) pop.classList.add("combo-big");
    pop.textContent = t("game.combo", { n: chainLevel });
    wrap.appendChild(pop);
    setTimeout(() => pop.remove(), 950);

    // Border wave — a soft light pulse sweeping from the edges of the board
    // toward the center, escalating with chain depth.
    const wave = el("div", `combo-wave wave-${Math.min(chainLevel, 5)}`);
    wrap.appendChild(wave);
    setTimeout(() => wave.remove(), 700);

    // Color flash overlaying the board, more saturated with deeper chains.
    const flash = el("div", `combo-flash flash-${Math.min(chainLevel, 5)}`);
    wrap.appendChild(flash);
    setTimeout(() => flash.remove(), 500);

    // Board zoom-in for big chains (3+) — a subtle scale-up + return to give
    // the merge weight without disrupting input handling.
    if (chainLevel >= 3) {
      const board = wrap.querySelector<HTMLElement>(".board-svg");
      if (board) {
        const scale = chainLevel >= 5 ? 1.08 : chainLevel >= 4 ? 1.06 : 1.04;
        board.animate(
          [
            { transform: "scale(1)" },
            { transform: `scale(${scale})`, offset: 0.35 },
            { transform: "scale(1)" },
          ],
          { duration: 380, easing: "cubic-bezier(0.2, 0.7, 0.3, 1)" },
        );
      }
    }
  }

  private shake(chainLevel: number): void {
    if (!settings.get("screenShake")) return;
    const wrap = document.getElementById("board-wrap");
    if (!wrap) return;
    const cls = chainLevel >= 4 ? "shake-hard" : "shake";
    wrap.classList.remove("shake", "shake-hard");
    void wrap.getBoundingClientRect();
    wrap.classList.add(cls);
    setTimeout(() => wrap.classList.remove(cls), 360);
  }

  // ---- Finish / rewards ----
  private onFinish(): void {
    if (this.resultShown) return;
    this.resultShown = true;
    this.stopTimer();
    this.highlight();
    this.refreshAbilityButtons();

    const e = this.session.engine;
    const durationS = Math.round(this.session.stats.elapsedMs / 1000);
    progress.noteMaxTier(e.maxTierReached);

    // Daily-mission progress (every finished run counts toward today's missions).
    progress.recordMissions(
      todayKey(),
      {
        mode: this.daily ? "daily" : this.stage ? "campaign" : "endless",
        won: this.session.outcome === "won",
        score: e.score,
        maxTier: e.maxTierReached,
        maxChain: e.maxChain,
        merges: this.session.stats.merges,
        drops: e.drops,
      },
      progress.effectiveCols(settings.get("boardCols")),
    );

    if (this.daily) {
      sfxClear();
      haptic([20, 40, 20]);
      const dr = progress.recordDaily(this.daily.dateKey, e.score);
      void submitRun({
        mode: "daily",
        ref: this.daily.dateKey,
        score: e.score,
        maxTier: e.maxTierReached,
        drops: e.drops,
        durationS,
        cols: e.config.cols,
      });
      this.afterFinish();
      this.showResult({ daily: dr });
      return;
    }

    if (!this.stage) {
      progress.recordEndless(e.score, e.config.cols);
      sfxFail();
      void submitRun({ mode: "endless", ref: "", score: e.score, maxTier: e.maxTierReached, drops: e.drops, durationS, cols: e.config.cols });
      this.afterFinish();
      this.showResult({ won: false, endless: true });
      return;
    }

    const won = this.session.outcome === "won";
    if (won) {
      sfxClear();
      haptic([20, 40, 20]);
    } else {
      sfxFail();
    }
    const stars = this.session.stars();
    const reward = progress.recordStageResult(
      this.stage.id,
      won,
      stars,
      e.score,
      e.drops,
      e.config.cols,
      this.stage.rewards,
      this.stage.rewards.firstClear?.unlock,
    );
    progress.save();
    this.afterFinish();
    this.showResult({ won, endless: false, stars, reward });
  }

  /** Evaluate achievements for this run and toast any unlocks. */
  private afterFinish(): void {
    // Collection: remember which specials this player has now encountered.
    progress.noteSpecialsSeen(
      this.session.usedWild,
      this.session.usedBomb,
      (this.stage?.constraints.bosses?.length ?? 0) > 0,
    );
    const unlocked = progress.checkAchievements(
      this.session.engine.maxChain,
      this.session.engine.config.cols,
      [...this.session.abilityIdsUsed],
      this.session.usedWild,
      this.session.usedBomb,
    );
    if (unlocked.length) {
      showAchievementToasts(unlocked);
      this.topRefresh(); // stardust changed
    }
  }

  private showResult(opts: {
    won?: boolean;
    endless?: boolean;
    stars?: number;
    reward?: { stardust: number; xp: number; levelsGained: number };
    daily?: { reward: number; streak: number; firstToday: boolean };
  }): void {
    const backdrop = el("div", "modal-backdrop");
    const modal = el("div", "panel modal");

    if (opts.daily) {
      const dr = opts.daily;
      modal.innerHTML = `
        <h2>${t("daily.done")}</h2>
        <div class="big-score">${fmt(this.session.engine.score)}</div>
        <div class="sub">${t("daily.best", { n: fmt(progress.snapshot.dailyBest[this.daily!.dateKey] ?? 0) })}</div>
        ${
          dr.firstToday
            ? `<div class="rewards"><span class="rw"><span class="sd-icon">✦</span> +${dr.reward}</span></div>
               <div class="sub">🔥 ${t("daily.streak", { n: dr.streak })}</div>`
            : `<div class="sub">${t("daily.claimed")}</div>`
        }`;
    } else if (opts.endless) {
      modal.innerHTML = `
        <h2>${t("game.over")}</h2>
        <div class="sub">${t("game.over.desc")}</div>
        <div class="big-score">${fmt(this.session.engine.score)}</div>
        <div class="sub">${t("hud.best")} ${fmt(progress.snapshot.bestEndless)}</div>`;
    } else if (opts.won) {
      const stars = opts.stars ?? 0;
      let starHtml = '<div class="result-stars">';
      for (let i = 1; i <= 3; i++) {
        starHtml += `<span class="rstar ${i <= stars ? "on" : ""}" style="--d:${i * 0.18}s">★</span>`;
      }
      starHtml += "</div>";
      modal.innerHTML = `
        <h2>${t("stage.cleared")}</h2>
        ${starHtml}
        <div class="rewards">
          <span class="rw"><span class="sd-icon">✦</span> +${opts.reward?.stardust ?? 0}</span>
          <span class="rw">XP +${opts.reward?.xp ?? 0}</span>
        </div>
        ${opts.reward?.levelsGained ? `<div class="sub">${t("reward.levelup", { n: opts.reward.levelsGained })}</div>` : ""}`;
    } else {
      modal.innerHTML = `
        <h2>${t("stage.failed")}</h2>
        <div class="sub">${t("stage.failed.desc")}</div>`;
    }

    const btnRow = el("div", "modal-btns");
    // Puzzles chain through the puzzle list; campaign stages through the
    // campaign order. Puzzle unlocks are gated at the puzzle's own width.
    const next = this.puzzleId
      ? nextPuzzleId(this.puzzleId)
      : this.stage
        ? nextStageId(this.stage.id)
        : undefined;
    const nextDef = next ? (this.puzzleId ? getPuzzle(next) : getStage(next)) : undefined;
    const nextCols = this.puzzleId
      ? (nextDef?.gridCols ?? 4)
      : progress.effectiveCols(settings.get("boardCols"));
    const nextUnlocked = next
      ? progress.isUnlocked(next, nextDef?.unlock?.requiresStage, nextCols)
      : false;

    if (opts.won && next && nextUnlocked) {
      const nextBtn = el("button", "btn-primary", t("stage.next"));
      nextBtn.onclick = () => {
        backdrop.remove();
        // Puzzles route through nav so the persisted route follows the chain.
        if (this.puzzleId) {
          this.nav.playPuzzle(next);
          return;
        }
        this.stage = getStage(next) ?? null;
        // Keep the same chosen board width across stages.
        const cols = progress.effectiveCols(settings.get("boardCols"));
        this.mode = this.stage
          ? { kind: "campaign", stage: this.stage, cols }
          : { kind: "endless", cols };
        this.start();
      };
      btnRow.appendChild(nextBtn);
    }
    const retry = el("button", opts.won ? "" : "btn-primary", t("game.restart"));
    retry.onclick = () => {
      backdrop.remove();
      this.start();
    };
    const home = el("button", "", t("menu.home"));
    home.onclick = () => {
      backdrop.remove();
      if (this.puzzleId) this.nav.puzzles();
      else if (this.stage) this.nav.campaign();
      else this.nav.home();
    };
    btnRow.append(retry, home);
    modal.appendChild(btnRow);
    backdrop.appendChild(modal);
    this.host.appendChild(backdrop);
  }
}
